/*
  LASE — day/night image system on product cards in collection lists.

  For each <product-card> that carries data-lase-day-url AND/OR
  data-lase-night-url, swap the visible <img> src based on the current
  html.is-dark state and on pointer hover.

    light + no-hover  →  day image
    light + hover     →  night image
    dark  + no-hover  →  night image
    dark  + hover     →  day image

  The wow effect is "the lights come on / off" when the user toggles
  the day/night switch — every card across the listing flips together.

  Implementation:
    - Cache the original src so we can restore on theme-toggle if one
      side is missing (graceful fallback to the originally-rendered
      Savor image)
    - Set src directly; CSS adds a fade animation
    - MutationObserver on <html> class flips every card together when
      the user toggles light/dark — no event bus needed
*/
(function () {
  'use strict';

  const ATTR_DAY   = 'data-lase-day-url';
  const ATTR_NIGHT = 'data-lase-night-url';

  function pickUrl (card) {
    const isDark = document.documentElement.classList.contains('is-dark');
    const day    = card.getAttribute(ATTR_DAY)   || '';
    const night  = card.getAttribute(ATTR_NIGHT) || '';
    if (isDark) return night || day;          // dark → night first, fall back to day
    return day || night;                       // light → day first, fall back to night
  }
  function pickHoverUrl (card) {
    // Hover swap = the OPPOSITE of the current default. With only
    // one side uploaded, hover is a no-op (returns empty).
    const isDark = document.documentElement.classList.contains('is-dark');
    const day    = card.getAttribute(ATTR_DAY)   || '';
    const night  = card.getAttribute(ATTR_NIGHT) || '';
    if (isDark) return day;
    return night;
  }

  function targetImg (card) {
    // First <img> inside the card gallery is the visible product
    // image. (Savor renders multiple <img> when product.media has
    // multiple, but the first is the one painted by default and is
    // what we swap.)
    return card.querySelector('.card-gallery img, .product-card img');
  }

  function applyMode (card) {
    const img = targetImg(card);
    if (!img) return;
    const url = pickUrl(card);
    if (!url) return;
    if (img.src === url) return;
    // Remember the original Savor-rendered src once, so we never
    // accidentally lose track of it across class-flip events.
    if (!img.dataset.laseOriginalSrc) img.dataset.laseOriginalSrc = img.src;
    img.src = url;
    if (img.srcset) img.srcset = '';   // avoid the responsive set fighting our manual src
  }

  function applyHover (card, hovering) {
    const img = targetImg(card);
    if (!img) return;
    const url = hovering ? pickHoverUrl(card) : pickUrl(card);
    if (!url) return;
    if (img.src === url) return;
    img.src = url;
    if (img.srcset) img.srcset = '';
  }

  function setupCard (card) {
    if (card.dataset.laseDaynightBound) return;
    if (!card.hasAttribute(ATTR_DAY) && !card.hasAttribute(ATTR_NIGHT)) return;
    card.dataset.laseDaynightBound = '1';

    // Initial paint
    applyMode(card);

    // Hover swap (skip on touch — typical pattern)
    card.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'touch') return;
      applyHover(card, true);
    });
    card.addEventListener('pointerleave', () => applyHover(card, false));
  }

  function setupAll () {
    document.querySelectorAll('product-card').forEach(setupCard);
  }

  function watchThemeToggle () {
    // Re-apply default mode to every bound card whenever html.is-dark
    // flips. The MutationObserver fires before the next paint, so the
    // CSS fade-in keyframe (declared globally) catches the transition.
    new MutationObserver(() => {
      document.querySelectorAll('product-card').forEach((card) => {
        if (card.dataset.laseDaynightBound) applyMode(card);
      });
    }).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  function init () {
    setupAll();
    watchThemeToggle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-scan after Shopify section reloads (theme editor + AJAX
  // collection filtering both rebuild product card DOM).
  document.addEventListener('shopify:section:load', setupAll);
})();
